import { cpf, cnpj } from 'cpf-cnpj-validator';
import { EPixKeyType } from 'types/banking';

export function getPixKeyType(key: string): EPixKeyType {
  const value = key.trim();

  // EMAIL
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return EPixKeyType.EMAIL;
  }

  // RANDOM (UUID) - check before removing non-numeric chars
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
    return EPixKeyType.RANDOM;
  }

  // Remove non-numeric characters for CPF/CNPJ/Phone detection
  const onlyNumbers = value.replace(/\D/g, '');

  // PHONE (Brazilian format: 55 + DDD (2 digits) + 9 + 8 digits = 13 digits)
  // or DDD + 9 + 8 digits = 11 digits
  if (onlyNumbers.length === 13 || onlyNumbers.length === 11) {
    // Check if it starts with 55 (country code) or is 11 digits (local format)
    const startsWithCountryCode = onlyNumbers.length === 13 && onlyNumbers.startsWith('55');
    const isLocalFormat = onlyNumbers.length === 11;

    if (startsWithCountryCode || isLocalFormat) {
      // Additional check: 9th digit should be 9 for mobile numbers
      const phoneDigits = startsWithCountryCode ? onlyNumbers.substring(2) : onlyNumbers;
      if (phoneDigits.length === 11 && phoneDigits.charAt(2) === '9') {
        return EPixKeyType.PHONE;
      }
    }
  }

  // CPF (11 digits)
  if (onlyNumbers.length === 11) {
    return EPixKeyType.CPF;
  }

  // CNPJ (14 digits)
  if (onlyNumbers.length === 14) {
    return EPixKeyType.CNPJ;
  }

  throw new Error('Chave Pix inválida');
}

export function isValidPixKey(value: string): boolean {
  if (!value || value.trim().length === 0) return false;

  const cleanKey = value.trim();

  // EMAIL
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailRegex.test(cleanKey)) return true;

  // CPF (validates with or without mask)
  if (cpf.isValid(cleanKey)) return true;

  // CNPJ (validates with or without mask)
  if (cnpj.isValid(cleanKey)) return true;

  // PHONE (Brazilian format with or without mask)
  const phoneNumbers = cleanKey.replace(/\D/g, '');
  if (phoneNumbers.length === 13 || phoneNumbers.length === 11) {
    const startsWithCountryCode = phoneNumbers.length === 13 && phoneNumbers.startsWith('55');
    const isLocalFormat = phoneNumbers.length === 11;

    if (startsWithCountryCode || isLocalFormat) {
      const phoneDigits = startsWithCountryCode ? phoneNumbers.substring(2) : phoneNumbers;
      if (phoneDigits.length === 11 && phoneDigits.charAt(2) === '9') {
        return true;
      }
    }
  }

  // RANDOM KEY (UUID v4)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(cleanKey)) return true;

  return false;
}

export function cleanPixKey(key: string): string {
  if (!key) return key;

  const trimmedKey = key.trim();

  try {
    const keyType = getPixKeyType(trimmedKey);

    switch (keyType) {
      case EPixKeyType.CPF:
      case EPixKeyType.CNPJ:
      case EPixKeyType.PHONE:
        // Remove all non-numeric characters
        return trimmedKey.replace(/\D/g, '');

      case EPixKeyType.EMAIL:
      case EPixKeyType.RANDOM:
        // Keep original value
        return trimmedKey;

      default:
        return trimmedKey;
    }
  } catch {
    // If key type cannot be determined, return as is
    return trimmedKey;
  }
}
